// src/pages/MPFlow/components/EddySegmentation.jsx
import React, { useState, useEffect, useCallback } from 'react';
import LocationSelector from './targeting/LocationSelector';
import AgeRangeSlider from './targeting/AgeRangeSlider';
import GenderSelector from './targeting/GenderSelector';
import InterestTagsInput from './targeting/InterestTagsInput';
import BudgetInput from './targeting/BudgetInput';

/* ── TypeWriter reutilizado ──────────────────────── */
function TypeWriter({ text, onDone, speed = 18 }) {
  const [displayed, setDisplayed] = useState('');
  useEffect(() => {
    setDisplayed('');
    let i = 0;
    const iv = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) { clearInterval(iv); onDone?.(); }
    }, speed);
    return () => clearInterval(iv);
  }, [text, onDone, speed]);
  return <span>{displayed}{displayed.length < text.length && <span className="edy-cursor">|</span>}</span>;
}

/* ── Etapas de segmentação ───────────────────────── */
const STEPS = [
  { key: 'location',  question: 'Em qual região você quer anunciar?' },
  { key: 'age',       question: 'Qual faixa de idade do seu público?' },
  { key: 'gender',    question: 'Seu produto é para qual público?' },
  { key: 'interests', question: 'Quais interesses descrevem seu cliente?' },
  { key: 'budget',    question: 'Quanto você quer investir por dia?' },
];

const GREETING = 'Agora vamos escolher quem verá seu anúncio.';
const FINAL_MESSAGE = 'Perfeito! Vou preparar sua campanha.';
const THINKING_MSG = 'Eddy está analisando...';

export default function EddySegmentation({ profile, onComplete }) {
  const [stepIndex, setStepIndex] = useState(-1); // -1 = greeting
  const [targeting, setTargeting] = useState({
    location: '', location_key: '', location_type: '',
    age_min: 18, age_max: 65,
    gender: 'all',
    interests: [],
    daily_budget: 20,
  });
  const [inputEnabled, setInputEnabled] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [done, setDone] = useState(false);
  const [bubbleText, setBubbleText] = useState(GREETING);

  // Após greeting, mostrar primeira pergunta
  useEffect(() => {
    const timer = setTimeout(() => {
      setStepIndex(0);
      setBubbleText(STEPS[0].question);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleTypingDone = useCallback(() => {
    if (!done && !thinking) setInputEnabled(true);
  }, [done, thinking]);

  const advance = useCallback(() => {
    setInputEnabled(false);
    setThinking(true);
    setTimeout(() => {
      setThinking(false);
      const nextIndex = stepIndex + 1;
      if (nextIndex < STEPS.length) {
        setStepIndex(nextIndex);
        setBubbleText(STEPS[nextIndex].question);
      } else {
        setDone(true);
        setBubbleText(FINAL_MESSAGE);
        setTimeout(() => onComplete?.(targeting), 2000);
      }
    }, 800);
  }, [stepIndex, targeting, onComplete]);

  const handleConfirm = () => {
    if (!inputEnabled || thinking || done) return;
    const step = STEPS[stepIndex];
    // Validação mínima por step
    if (step.key === 'location' && !targeting.location) return;
    if (step.key === 'interests' && targeting.interests.length === 0) return;
    advance();
  };

  const currentStep = STEPS[stepIndex];

  /* ── Resumo compacto das respostas anteriores ──── */
  const summaryItems = [];
  if (targeting.location && stepIndex > 0) summaryItems.push({ label: 'Local', val: targeting.location });
  if (stepIndex > 1) summaryItems.push({ label: 'Idade', val: `${targeting.age_min} – ${targeting.age_max === 65 ? '65+' : targeting.age_max}` });
  if (stepIndex > 2) summaryItems.push({ label: 'Gênero', val: targeting.gender === 'all' ? 'Todos' : targeting.gender === 'male' ? 'Homens' : 'Mulheres' });
  if (targeting.interests.length > 0 && stepIndex > 3) summaryItems.push({ label: 'Interesses', val: targeting.interests.slice(0, 3).join(', ') + (targeting.interests.length > 3 ? '...' : '') });

  return (
    <div className="edy-assistant">
      {/* Eddy Avatar */}
      <div className="edy-avatar">
        <img src="/robot.png" alt="Eddy" className="edy-avatar__img" />
      </div>

      {/* Speech Bubble */}
      {thinking ? (
        <div className="edy-bubble edy-bubble--thinking">
          <span className="edy-bubble__dots"><span /><span /><span /></span>
          <span className="edy-bubble__thinking-text">{THINKING_MSG}</span>
        </div>
      ) : (
        <div className="edy-bubble">
          <TypeWriter text={bubbleText} onDone={handleTypingDone} key={bubbleText} />
        </div>
      )}

      {/* Respostas anteriores */}
      {summaryItems.length > 0 && (
        <div className="edy-assistant__answers">
          {summaryItems.map((item) => (
            <div key={item.label} className="edy-assistant__answer-chip">
              <span className="edy-assistant__answer-label">{item.label}</span>
              {item.val}
            </div>
          ))}
        </div>
      )}

      {/* Input da etapa atual */}
      {!done && !thinking && inputEnabled && currentStep && (
        <div className="seg-step-input">
          {currentStep.key === 'location' && (
            <LocationSelector
              value={targeting}
              onChange={(loc) => setTargeting((prev) => ({ ...prev, ...loc }))}
            />
          )}

          {currentStep.key === 'age' && (
            <AgeRangeSlider
              min={targeting.age_min}
              max={targeting.age_max}
              onChange={({ age_min, age_max }) => setTargeting((prev) => ({ ...prev, age_min, age_max }))}
            />
          )}

          {currentStep.key === 'gender' && (
            <GenderSelector
              value={targeting.gender}
              onChange={(gender) => setTargeting((prev) => ({ ...prev, gender }))}
            />
          )}

          {currentStep.key === 'interests' && (
            <InterestTagsInput
              value={targeting.interests}
              onChange={(interests) => setTargeting((prev) => ({ ...prev, interests }))}
            />
          )}

          {currentStep.key === 'budget' && (
            <BudgetInput
              value={targeting.daily_budget}
              onChange={(daily_budget) => setTargeting((prev) => ({ ...prev, daily_budget }))}
            />
          )}

          <button
            className="mp-btn mp-btn--primary mp-mt-5"
            onClick={handleConfirm}
            type="button"
          >
            Confirmar →
          </button>
        </div>
      )}
    </div>
  );
}
